












import { Component, Vue, Watch, Prop, Mixins } from "vue-property-decorator";
import { Getter, Action, Mutation, namespace } from "vuex-class";
import { getMapStyle } from "@/scripts/mapping/mapStyle";
import { SVLocation, ILayerMap, MobileMarker } from "@/store/mobile/types";
import { Guard, PatrolZone } from "@/store/dispatch/types";
import { axiosInstance } from "@/axios.instance";
import { MapBounds } from "@/store/mobile/types";
import { AxiosResponse } from "axios";
import { stringToBounds, getEnhancedMapping, IEnhancedMapping, ILayer } from "@sureview/v2-mapping";
import getScript from "@/services/insertScript.service";
import GoogleMap from "@/scripts/mapping/map";
import MobileInfoWindow from "@/components/mobile/core/MobileInfoWindow.vue";

class LocationBounds {
	location: google.maps.LatLng;
	viewport: {
		northeast: google.maps.LatLng;
		southwest: google.maps.LatLng;
	};
}

const Mobile = namespace("mobile");
const MapLayers = namespace("mapLayers");

declare global {
	interface Window {
		infoWindow: any;
		closeInfoWindow: any;
		markerClickHandler: any;
	}
}

@Component
export default class MobileMap extends Vue {
	// Map type
	@Getter getMapType: any;
	@Getter getHideLabels: any;
	@Mutation setMapType: any;
	@Getter("getMapKey") private mapKey: string;

	// Call signs (guard specific)
	@Mobile.Mutation setProximityCallSigns: any;

	// Device location
	@Mobile.Getter("getDeviceLocation") deviceLocation: any;
	@Mobile.Getter("getShowDirections") userPrefShowDirections: boolean;
	@Mobile.Mutation public setDeviceLocation: any;
	@Mobile.Mutation setIsWatchingLocation: (value: boolean) => void;
	@Mobile.Action setLocationForGuard: (location: string) => void;

	@Mobile.Getter("getFocalPoint") focalPoint: any;	
	@Mobile.Mutation setAlmostArrived: (almostArrived: boolean) => void;
	@Mobile.Getter("getHasArrived") hasArrived: boolean;

	// Patrol zones
	@Mobile.State(state => state.PatrolZones) patrolZones!: PatrolZone[];
	@Mobile.Action loadPatrolZones: any;

	// Guards
	@Mobile.Action loadCallSigns: any;
	@Mobile.Getter("getCallSigns") callSigns!: Guard[];	
	@Mobile.State(state => state.MobileUser.manualLocationUpdate) manualLocationUpdate: boolean;

	// Map layers
	@MapLayers.Action public loadMapLayers: (bounds: MapBounds) => Promise<AxiosResponse<any>>;

	// Local vars
	public directionsService: google.maps.DirectionsService;
	public directionsDisplay: google.maps.DirectionsRenderer;
	public googleMap: GoogleMap | null = null;
	public enhancedMapping: IEnhancedMapping | null = null;
	public mapType: string = "";
	public minPatrolZones: number = 1;
	private mapInitialized: boolean = false;
	private userPreviousLocation: google.maps.LatLng;
	private userPreviousDestination: google.maps.LatLng;
	private deviceMarker: google.maps.Marker;
	private destination?: google.maps.LatLng | null;
	private infoWindowComponent: MobileInfoWindow;
	private lastUpdatedGuardLocation = Date.now();
	private geolocationWatchNumber: number;
	private layersAdded: ILayerMap = {};
	private guardElevation: number = 0;
	private mobileMarkers: MobileMarker[] = [];

	@Prop({ default: () => 17 })
	public zoomLevel;

	@Prop({ default: () => false })
	public prototype: boolean;

	@Prop({ default: () => null })
	public guardLocationForManualUpdate: any;

	@Watch("guardLocationForManualUpdate")
	onGuardLocationForManualUpdateChanged(newLocation) {
		if (newLocation) {
			this.guardElevation = newLocation.minElevation;
			if (!newLocation.isCorrectLocation) {
				this.updateGuardLocationManually();
			}
		}
	}

	@Prop({ default: () => true })
	public drawPatrolZones: boolean;

	@Prop({ default: () => [] })
	public mapMarkersToDisplay: MobileMarker[];

	@Watch("mapMarkersToDisplay")
	private onMapMarkersToDisplay(mobileMarkers: MobileMarker[]) {
		this.displayMarkers(mobileMarkers);
	}

	@Prop({ default: () => false })
	public closeInfoWindow: boolean;

	@Watch("closeInfoWindow")
	public onCloseInfoWindow() {
		try {
			if (window.infoWindow != null) {
				window.infoWindow.close();
			}
		} catch (e) {
			console.error("Failed to close info window");
		}
	}

	@Prop({ default: () => null })
	public showDirectionsToLocation: google.maps.LatLng;

	@Watch("showDirectionsToLocation")
	private onShowDirectionsToLocation(destination: google.maps.LatLng) {
		this.destination = destination;
		this.showDirections(this.deviceLocation, destination);
	}

	@Prop({ default: () => false})
	public hideDirections: boolean;

	@Watch("hideDirections")
	private onHideDirections(){
		if (this.directionsDisplay && this.directionsDisplay.getMap()) {
			this.directionsDisplay.set("directions", null);
			this.directionsDisplay.setMap(null);
		}

		this.userPreviousDestination = new google.maps.LatLng(0, 0);
	}	

	@Watch("manualLocationUpdate")
	onManualLocationUpdateChanged(newValue: boolean, oldValue: boolean) {
		if (newValue && newValue != oldValue) {
			this.disableGPS();
			this.onHideDirections();
		} else if (!newValue && newValue != oldValue) {
			this.enableGPS();
		}
	}

	@Watch("focalPoint")
	private onFocalPointChanged(locationFocus: google.maps.LatLng) {
		this.onCloseInfoWindow();
		this.panToCenter(locationFocus);
	}

	@Watch("deviceLocation")
	onDeviceLocationChanged(value: string, oldValue: string) {
		if (typeof value !== "undefined" && value != null) {
			this.updatePatrolZones();
		}
	}

	@Watch("patrolZones")
	onPatrolZonesFetch() {
		if (this.deviceLocation !== "") {
			this.updatePatrolZones();
		}
	}

	@Watch("userPrefShowDirections")
	onUserPrefShowDirections(value: boolean) {
		if (value === false) {
			this.onHideDirections();
		} else {
			this.showDirections(this.deviceLocation, this.destination);
		}
	}

	async mounted() {
		this.init();
		this.setMapType("roadmap");
		await this.loadCallSigns();
		await this.loadPatrolZones();

		if (this.prototype) {
			setTimeout(() => {
				this.foundLocation(null);
			}, 3000);
		}
	}

	async init() {
		await getScript(
			`https://maps.googleapis.com/maps/api/js?key=` +
				`${this.mapKey}` +
				`&libraries=drawing,places,geometry`
		);

		// Create the directions service
		this.directionsService = new google.maps.DirectionsService();
		this.directionsDisplay = new google.maps.DirectionsRenderer({
			suppressMarkers: true,
			preserveViewport: true,
			polylineOptions: {
				strokeColor: "#117C8F",
				strokeWeight: 5
			}
		});

		// Default position of the map is 0, 0
		let pos: LocationBounds = {
			location: new google.maps.LatLng(0, 0),
			viewport: null
		};

		// If we're not in prototype mode, use the device's location (GPS)
		if (!this.prototype) {
			this.enableGPS();
		}

		// Remove all transit stations from the map (too much for mobile interface)
		var mapStyles = getMapStyle(this.getMapType, this.getHideLabels);
		mapStyles.push({
			featureType: "transit.station",
			stylers: [{ visibility: "off" }]
		});

		// Create the Google Map
		this.googleMap = new GoogleMap(
			this.$refs.mappingContainer as HTMLElement,
			pos.location,
			this.zoomLevel,
			false,
			this.getMapType,
			mapStyles,
			true
		);

		// EnhancedMapping is used for drawing SureView MapLayers
		this.enhancedMapping = await getEnhancedMapping("google", this.mapKey);

		// Fit the bounds of the map
		if (pos.viewport) {
			this.googleMap.map.fitBounds(new google.maps.LatLngBounds(pos.viewport.southwest, pos.viewport.northeast));
		}

		this.userPreviousLocation = new google.maps.LatLng(0, 0);
		this.userPreviousDestination = new google.maps.LatLng(0, 0);
	}

	async beforeDestroy() {
		// Clear the GPS watch so we're no longer doing it when the map is cleared or the page changes
		this.disableGPS();
	}

	/*** MAP Functions ***/
	showDirections(origin: google.maps.LatLng, destination: google.maps.LatLng) {
		this.userPreviousLocation = origin;

		if (!origin || !destination) {
			return;
		}

		// Calculate if the user's position is near the marker
		var distanceBetweenOriginDestination = google.maps.geometry.spherical.computeDistanceBetween(
			new google.maps.LatLng(origin.lat(), origin.lng()),
			new google.maps.LatLng(destination.lat(), destination.lng())
		);

		if (distanceBetweenOriginDestination < 50) {
			if (!this.hasArrived) {
				this.setAlmostArrived(true);
				this.onHideDirections();
			} else {
				this.setAlmostArrived(false);
			}
		}

		// Don't go further if the user has turned off directions
		if (!this.userPrefShowDirections) {
			return;
		}

		var request: google.maps.DirectionsRequest = {
			origin: origin,
			destination: destination,
			travelMode: google.maps.TravelMode["WALKING"]
		};

		this.directionsService.route(
			request,
			function(result, status) {
				if (status == google.maps.DirectionsStatus.OK) {
					this.directionsDisplay.setMap(this.googleMap.map);
					this.directionsDisplay.setDirections(result);
				}
			}.bind(this)
		);
	}

	panToCenter(centerPosition: google.maps.LatLng) {
		if (this.googleMap) {
			this.googleMap.map.panTo(centerPosition);
		}
	}

	parseLatLngToLocation(location: google.maps.LatLng): string {
		if (!location) {
			return null;
		}

		let locationJson: SVLocation = {
			lat: location.lat(),
			lng: location.lng()
		};

		return JSON.stringify(locationJson);
	}

	/*** IMMIX Feature Functions ***/
	updatePatrolZones() {
		if(!this.drawPatrolZones) return;

		if (this.patrolZones.length < 1) return;

		if (this.patrolZones.length < this.minPatrolZones) return;

		this.updateCallSignList();

		this.patrolZones.forEach(zone => {
			var boundaryPoints = JSON.parse(zone.zonePath);
			var boundarydata = new Array();
			boundaryPoints.forEach(point => {
				boundarydata.push(new google.maps.LatLng(point["lat"], point["lng"]));
			});

			var boundaryPolygon = new google.maps.Polygon({
				paths: boundarydata,
				strokeColor: "Red",
				strokeOpacity: 0.8,
				strokeWeight: 2,
				fillColor: "White",
				fillOpacity: 0.1
			});
			boundaryPolygon.setMap(this.googleMap.map);
			this.minPatrolZones += 1;
		});
	}

	updateCallSignList() {
		this.setProximityCallSigns([]);

		var guardCallSigns = [];
		this.callSigns.forEach(callSign => {
			try {
				if (callSign.lastLocation !== null && callSign.lastLocation !== "NUL") {
					const parsedLatLong = JSON.parse(callSign.lastLocation);

					var guardPoint = new google.maps.LatLng(parsedLatLong.lat, parsedLatLong.lng);

					if (google.maps.geometry.spherical.computeDistanceBetween(guardPoint, this.deviceLocation) < 1000) {
						guardCallSigns.push(callSign);
					}
				}
			} catch (e) {
				console.error(`Failed to update callsign list ${e}`);
			}
		});

		this.setProximityCallSigns(guardCallSigns);
	}

	async displayMapLayers(bounds: MapBounds) {
		this.clearMapLayers();

		const loadMapLayersResponse = await this.loadMapLayers(bounds);

		var mapData = loadMapLayersResponse.data;

		const elevations = mapData.elevations.slice();

		if (mapData.layers) {
			const itemLayerIDs: string[] = [];

			mapData.layers.forEach(layer => {
				const elevation = bounds.elevation;

				let isElevated =
					(layer.minElevation == null || layer.minElevation! <= elevation) &&
					(layer.maxElevation == null || layer.maxElevation! >= elevation);

				if (isElevated) {
					itemLayerIDs.push(layer.mapLayerID.toString());

					const imageEndpoint = axiosInstance.defaults.baseURL.endsWith("/")
						? axiosInstance.defaults.baseURL + "MapLayers/Image/" + layer.mapLayerID.toString()
						: axiosInstance.defaults.baseURL + "/MapLayers/Image/" + layer.mapLayerID.toString();

					if (this.layersAdded[layer.mapLayerID] == undefined) {
						this.layersAdded[layer.mapLayerID] = this.enhancedMapping.createOverlay(
							this.googleMap,
							layer.mapLayerID,
							layer.title,
							stringToBounds(layer.ne, layer.sw),
							layer.rotation,
							imageEndpoint,
							layer.minElevation,
							layer.maxElevation,
							true
						);
					}
				}
			});
		}
	}

	clearMapLayers() {
		for (var layerId in this.layersAdded) {
			let layer = this.layersAdded[layerId];

			layer.remove();
			delete this.layersAdded[layerId];
		}
	}

	/*** MARKER Functions ***/
	displayMarkers(markers: MobileMarker[]){
		markers.forEach(
			function(marker) {
				var existingMarker = this.mobileMarkers.find(x => x.id === marker.id);

				if (!existingMarker) {
					this.createMarker(marker);
				} 
				else {
				 	if (existingMarker.googleMarker) {
				 		existingMarker.googleMarker.setIcon(this.makeMarkerIcon(marker.iconURL));
				 	}
				}
			}.bind(this)
		);

		// Remove all markers that are currently displayed but shouldn't be
		this.mobileMarkers.forEach(marker => {
			var existingElement = markers.find(x => x.id === marker.id);

			if (!existingElement) {
				this.removeMarker(marker.id);
			}
		});
	}

	createMarker(mobileMarker: MobileMarker) {
		if (!this.googleMap) {
			return;
		}

		// Copy the marker so we can add a google marker
		var markerToAdd: MobileMarker = {
			id : mobileMarker.id,
			title : mobileMarker.title,
			location : mobileMarker.location,
			iconURL : mobileMarker.iconURL,
			color: mobileMarker.color,
			infoWindow: mobileMarker.infoWindow,
			googleMarker : null,
			object : null
		};

		// Check here for existing marker at this location

		// Make the marker icon
		var markerIcon = {
			url: mobileMarker.iconURL,
			scaledSize: new google.maps.Size(32, 32), // scaled size
			origin: new google.maps.Point(0, 0), // origin
			anchor: new google.maps.Point(16, 16)
		};

		// Make the marker for the map
		var googleMarker = new google.maps.Marker({
			position: mobileMarker.location,
			map: this.googleMap.map,
			title: mobileMarker.title,
			icon: markerIcon
		});

		// Add a circle of a 50 metre radius around the point
		var markerCircle = new google.maps.Circle({
			strokeColor: mobileMarker.color,
			strokeOpacity: 0.8,
			strokeWeight: 2,
			fillColor: mobileMarker.color,
			fillOpacity: 0.05,
			map: this.googleMap.map,
			center: mobileMarker.location,
			radius: 50
		});

		// Add the circle to the marker object
		googleMarker.set("circle", markerCircle);

		googleMarker.addListener("click", function() {
			// Doing this to bring everything back on to the main thread
			window.markerClickHandler(mobileMarker.id);
		});

		// Save the info window to a global variable
		this.infoWindowComponent = mobileMarker.infoWindow;
		this.infoWindowComponent.$on("closeInfoWindow", this.onCloseInfoWindow);

		// Make the popup for the marker when clicked
		var markerInfoWindow = new google.maps.InfoWindow({
			content: this.infoWindowComponent.$el
		});

		// Assign the global variables
		window.closeInfoWindow = this.onCloseInfoWindow;
		window.infoWindow = markerInfoWindow;
		window.markerClickHandler = this.markerClickHandler;

		markerToAdd.googleMarker = googleMarker;
		this.mobileMarkers.push(markerToAdd);
	}

	removeMarker(id: number) {
		// Get the marker from the array
		const marker = this.getMarker(id);

		if (marker.googleMarker) {
			// Remove the marker from the map by setting it to null
			marker.googleMarker.setMap(null);

			// Remove the marker circle
			var markerCircle = marker.googleMarker.get("circle");
			if (markerCircle) {
				markerCircle.setMap(null);
			}

			// Remove the marker from the array
			this.mobileMarkers = this.mobileMarkers.filter(x => x.id !== id);
		}
	}

	getMarker(id: number): MobileMarker {
		const marker = this.mobileMarkers.find(element => element.id === id);
		return marker;
	}

	markerClickHandler(id: number) {
		// If the info window is currently open, close it
		if (window.infoWindow != null) {
			window.infoWindow.close();
		}

		// Update the info window's contents
		window.infoWindow.setContent(this.infoWindowComponent.$el);
		this.infoWindowComponent.openInfoWindow(id);

		// Display the info window
		const marker = this.getMarker(id);
		window.infoWindow.open(this.googleMap.map, marker.googleMarker);
	}

	makeMarkerIcon(urlIcon: string = null) {
		if (!urlIcon){
			urlIcon = "images/enhancedmapping/bell-solid.svg";
		}

		var markerIcon = {
			url: urlIcon,
			scaledSize: new google.maps.Size(32, 32), // scaled size
			origin: new google.maps.Point(0, 0), // origin
			anchor: new google.maps.Point(16, 16)
		};

		return markerIcon;
	}

	/*** GPS Functions ***/
	enableGPS() {
		if (this.prototype) {
			return;
		}

		if (navigator.geolocation) {
			const options = {
				enableHighAccuracy: true,
				timeout: 15000
			};

			// Then set up a watcher for when the device moves
			this.geolocationWatchNumber = navigator.geolocation.watchPosition(
				this.foundLocation,
				this.locationError,
				options
			);
			this.setIsWatchingLocation(true);
		}
	}

	disableGPS() {
		if (this.prototype) {
			return;
		}

		navigator.geolocation.clearWatch(this.geolocationWatchNumber);
		this.setIsWatchingLocation(false);
	}

	public foundLocation(position) {
		let deviceLocation: google.maps.LatLng = null;
		if (this.prototype) {
			// 51°37'15.0"N 3°56'31.1"W 51.620075,-3.947882
			deviceLocation = new google.maps.LatLng(51.517905, -3.180747); // Hard coded location (Swansea Office) to use for debugging
			//deviceLocation = new google.maps.LatLng(51.517894, -3.180879);
		} else {
			deviceLocation = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);
		}

		// Update vuex with the current device location
		this.setDeviceLocation(deviceLocation);
		this.updateLocationHistory(deviceLocation);

		if (this.googleMap) {
			if (!this.mapInitialized) {
				this.googleMap.map.setCenter(deviceLocation);
				var markerIcon = {
					url: "images/google-maps-location-32x32.png",
					scaledSize: new google.maps.Size(32, 32),
					origin: new google.maps.Point(0, 0),
					anchor: new google.maps.Point(16, 16)
				};
				this.deviceMarker = new google.maps.Marker({
					position: deviceLocation,
					map: this.googleMap.map,
					icon: markerIcon
				});
				this.mapInitialized = true;
			} else {
				this.deviceMarker.setPosition(deviceLocation);

				// Don't show directions if guard status is 'Arrived'
				if (this.hasArrived) {
					this.onHideDirections();
					return;
				} else if (
					// Check user has moved more than 20 metres since the last time this was called
					this.userPreviousLocation &&
					this.deviceLocation &&
					google.maps.geometry.spherical.computeDistanceBetween(this.userPreviousLocation, this.deviceLocation) > 20
				) {
					if (this.destination !== null) {
						this.showDirections(this.deviceLocation, this.destination);
					}
				}
			}
		}
	}

	locationError(error) {
		console.error(`Location error: ${error.code} ${error.message}`);
	}

	updateLocationHistory(deviceLocation: google.maps.LatLng) {
		// On first run (usually) this.lastUpdatedGuardLocation won't have been set, so set it
		if (this.lastUpdatedGuardLocation === null || typeof this.lastUpdatedGuardLocation === "undefined") {
			this.lastUpdatedGuardLocation = Date.now();
		}

		// Update the backend with the device location (every 5 seconds, so less regularly than the Vuex store)
		if (Date.now() - this.lastUpdatedGuardLocation > 5000) {
			if (deviceLocation) {
				const deviceLocationString = this.parseLatLngToLocation(deviceLocation);
				this.setLocationForGuard(deviceLocationString);
				this.lastUpdatedGuardLocation = Date.now();
			}
		}
	}

	async updateGuardLocationManually() {
		if (!this.googleMap) {
			return;
		}

		this.setDeviceLocation(this.guardLocationForManualUpdate.location);
		this.updateLocationHistory(this.guardLocationForManualUpdate.location);
		this.googleMap.map.setCenter(this.guardLocationForManualUpdate.location);
		this.deviceMarker.setPosition(this.guardLocationForManualUpdate.location);

		let boundaries = this.googleMap.bounds.raw.toJSON();
		const North = boundaries.north;
		const West = boundaries.west;
		const East = boundaries.east;
		const South = boundaries.south;

		await this.displayMapLayers({
			north: North,
			east: East,
			south: South,
			west: West,
			elevation: this.guardElevation
		});
	}
}
